// assets/AllCourses/Exercises/MissingAlphabetsExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MissingAlphabetsExercise({
  exercise = {},
  onUpdate,           // Edit mode only
  errors = {},
  isPreview = false,  // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
}) {
  const isEditMode = typeof onUpdate === "function";

  // Safe questions
  const questions = Array.isArray(exercise.missingAlphabetsQuestions)
    ? exercise.missingAlphabetsQuestions
    : [];

  // Student state
  const [state, setState] = useState({
    currentIndex: 0,
    answers: [], // 2D array: answers[qIndex][blankIndex]
    hasInteracted: false,
  });
  const completedRef = useRef(false);

  // Initialize student answers
  useEffect(() => {
    if (isPreview) {
      const initialAnswers = questions.map(q =>
        Array((q.word.match(/_+/g) || []).length).fill("")
      );

      if (exercise.attempt && Array.isArray(exercise.attempt.answers)) {
        setState({
          currentIndex: exercise.attempt.currentIndex || 0,
          answers: exercise.attempt.answers,
          hasInteracted: !!exercise.attempt.hasInteracted,
        });
      } else {
        setState({
          currentIndex: 0,
          answers: initialAnswers,
          hasInteracted: false,
        });
      }
    }
  }, [isPreview, questions, exercise.attempt]);

  // Reset on exercise change
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  // Normalize for comparison
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "");
  };

  // === EDIT MODE FUNCTIONS ===
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("missingAlphabetsQuestions", next);
  };

  const handleWordChange = (qIdx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || { word: "", answer: [], requireCapitalization: false };

    const blankCount = (value.match(/_+/g) || []).length;
    const oldAnswers = Array.isArray(current.answer) ? current.answer : [];

    next[qIdx] = {
      ...current,
      word: value,
      answer: Array.from({ length: blankCount }, (_, i) => oldAnswers[i] || ""),
    };

    updateQuestions(next);
  };

  const handleAnswerChange = (qIdx, aIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { answer: [] };
    const answers = [...q.answer];
    answers[aIdx] = value;
    next[qIdx] = { ...q, answer: answers };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      { word: "", answer: [], requireCapitalization: false },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  // === STUDENT MODE FUNCTIONS ===
  const currentQuestion = questions[state.currentIndex];
  const currentBlanks = currentQuestion ? (currentQuestion.word.match(/_+/g) || []).length : 0;

  const handleInputChange = (blankIdx, value) => {
    if (isEditMode || showResult) return;

    setState(prev => {
      const next = structuredClone(prev);
      next.answers[state.currentIndex][blankIdx] = value;
      next.hasInteracted = true;

      setAttempt(next);

      const allFilled = next.answers.every(qAnswers =>
        qAnswers.every(a => a.trim() !== "")
      );

      if (allFilled) {
        checkAnswers(next);
      }

      return next;
    });
  };

  const nextQuestion = () => {
    if (state.currentIndex >= questions.length - 1) return;

    setState(prev => {
      const next = { ...prev, currentIndex: prev.currentIndex + 1 };
      setAttempt(next);
      return next;
    });
  };

  const checkAnswers = (currentState) => {
    if (completedRef.current || !isPreview) return;
    completedRef.current = true;

    const allCorrect = questions.every((q, qIdx) => {
      const blanks = (q.word.match(/_+/g) || []).length;
      return Array.from({ length: blanks }).every((_, aIdx) => {
        const user = String(currentState.answers[qIdx]?.[aIdx] || "").trim();
        const correct = String(q.answer[aIdx] || "").trim();
        if (q.requireCapitalization) return user === correct;
        return user.toLowerCase() === correct.toLowerCase();
      });
    });

    onComplete(exercise._id, allCorrect);
  };

  // === EDIT MODE RENDER ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No words yet. Click "Add Word" to begin.
          </p>
        )}

        {questions.map((q, qIdx) => {
          const blanks = (q.word.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6 space-y-6">
              <StringField
                label={`Word ${qIdx + 1} (use _ for each missing letter)`}
                value={q.word || ""}
                onChange={(v) => handleWordChange(qIdx, v)}
                placeholder="A__le"
                required
                error={errors?.[`exercise-${exercise.index}-ma-word-${qIdx}`]}
              />

              {/* Answers */}
              <div className="space-y-3">
                <label className="font-medium text-sm">Missing Letters</label>
                {blanks === 0 ? (
                  <p className="text-sm text-gray-500">
                    Use underscores (_) in the word to create blanks
                  </p>
                ) : (
                  Array.from({ length: blanks }).map((_, aIdx) => (
                    <StringField
                      key={aIdx}
                      label={`Letter ${aIdx + 1}`}
                      value={q.answer[aIdx] || ""}
                      onChange={(v) => handleAnswerChange(qIdx, aIdx, v)}
                      placeholder="p"
                      maxLength={1}
                      required
                      error={errors?.[`exercise-${exercise.index}-ma-answer-${qIdx}`]}
                    />
                  ))
                )}
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) => handleCapitalizationChange(qIdx, c)}
              />

              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleRemoveQuestion(qIdx)}
              >
                Remove Word
              </Button>
            </Card>
          );
        })}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Word
        </Button>
      </div>
    );
  }

  // === STUDENT PREVIEW MODE ===
  if (questions.length === 0 || !currentQuestion) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No valid missing alphabet questions.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const parts = currentQuestion.word.split(/_+/g);
  let blankCounter = 0;

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardDescription>
          Fill in the missing letters • Question {state.currentIndex + 1} of {questions.length}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="p-8 bg-muted rounded-xl text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-3 text-4xl font-bold tracking-wider">
            {parts.map((part, i) => (
              <React.Fragment key={i}>
                <span>{part}</span>
                {i < currentBlanks && (
                  <Input
                    value={state.answers[state.currentIndex]?.[blankCounter] || ""}
                    onChange={(e) => handleInputChange(blankCounter, e.target.value)}
                    maxLength={1}
                    className={cn(
                      "w-16 h-16 text-center text-3xl font-bold",
                      showResult &&
                        normalize(state.answers[state.currentIndex][blankCounter], currentQuestion.requireCapitalization) ===
                        normalize(currentQuestion.answer[blankCounter], currentQuestion.requireCapitalization)
                        ? "bg-green-100 border-green-600"
                        : showResult && state.answers[state.currentIndex][blankCounter] &&
                          "bg-red-100 border-red-600"
                    )}
                    autoFocus={blankCounter === 0}
                    disabled={showResult}
                  />
                )}
                {blankCounter < currentBlanks && blankCounter++}
              </React.Fragment>
            ))}
          </div>

          {showResult && (
            <div className="mt-6 space-y-2">
              {Array.from({ length: currentBlanks }).map((_, i) => {
                const user = state.answers[state.currentIndex][i] || "";
                const correct = currentQuestion.answer[i] || "";
                const isCorrect = normalize(user, currentQuestion.requireCapitalization) ===
                                  normalize(correct, currentQuestion.requireCapitalization);

                return (
                  <p key={i} className={cn("text-lg", isCorrect ? "text-green-700" : "text-red-700")}>
                    Blank {i + 1}: {user || "(empty)"} → {isCorrect ? "Correct" : `Wrong — ${correct}`}
                  </p>
                );
              })}
            </div>
          )}
        </div>

        {state.currentIndex < questions.length - 1 && !showResult && (
          <div className="text-center">
            <Button
              size="lg"
              onClick={nextQuestion}
              disabled={state.answers[state.currentIndex].some(a => !a.trim())}
            >
              Next Question →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}